import React, { useCallback, useEffect, useRef, useState } from 'react';
import { STATUS_COLORS, type StatusVariant } from '../lib/statusColors';

// ---------------------------------------------------------------------------
// Minimal toast system (no external dependency).
//
// Used to surface async submission outcomes (Option B) to the user. Errors stay
// until dismissed; success auto-dismisses. Colors come from the shared
// STATUS_COLORS palette (same as <Alert>).
// ---------------------------------------------------------------------------

export type ToastVariant = StatusVariant;

export interface ToastItem {
  id: string;
  variant: ToastVariant;
  message: string;
}

export interface UseToasts {
  toasts: ToastItem[];
  push: (variant: ToastVariant, message: string, autoDismissMs?: number) => void;
  dismiss: (id: string) => void;
}

export function useToasts(): UseToasts {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  // Pending auto-dismiss timers, cleared on unmount so none fires setState on an
  // unmounted component.
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  useEffect(
    () => () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    },
    [],
  );

  const dismiss = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const push = useCallback(
    (variant: ToastVariant, message: string, autoDismissMs?: number): void => {
      const id = crypto.randomUUID();
      setToasts(prev => [...prev, { id, variant, message }]);
      if (autoDismissMs && autoDismissMs > 0) {
        timersRef.current.push(setTimeout(() => dismiss(id), autoDismissMs));
      }
    },
    [dismiss],
  );

  return { toasts, push, dismiss };
}

export const Toaster: React.FC<{
  toasts: ToastItem[];
  onDismiss: (id: string) => void;
}> = ({ toasts, onDismiss }) => {
  if (toasts.length === 0) return null;

  return (
    <div
      style={{
        position: 'fixed',
        right: '1rem',
        bottom: '1rem',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        maxWidth: 'min(28rem, calc(100vw - 2rem))',
      }}
    >
      {toasts.map(t => {
        const { bg, fg } = STATUS_COLORS[t.variant];
        return (
          <div
            key={t.id}
            role="status"
            style={{
              backgroundColor: bg,
              color: fg,
              padding: '0.75rem 1rem',
              borderRadius: '6px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.75rem',
              whiteSpace: 'pre-wrap',
              fontSize: '0.875rem',
              lineHeight: 1.4,
            }}
          >
            <span style={{ flex: 1 }}>{t.message}</span>
            <button
              onClick={() => onDismiss(t.id)}
              aria-label="Dismiss"
              style={{
                background: 'transparent',
                border: 'none',
                color: fg,
                cursor: 'pointer',
                fontSize: '1rem',
                lineHeight: 1,
                padding: 0,
              }}
            >
              ×
            </button>
          </div>
        );
      })}
    </div>
  );
};
